import { Check, LayoutGrid, Pencil, Tag } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { useEffect, useRef, useState } from "react";
import { useEntityManagerContext } from "../context/EntityManagerContext";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { cn } from "@/modern/utils";
import { ColorPicker } from "@/modern/components/color-picker";
import { Button } from "@/modern/components/ui/button";
import { EntityNameButton } from "./EntityNameButton";
import { AppActiveUsers } from "../../appHeader/headerButtons/appActiveUsers/AppActiveUsers";
import { useConfiguratorContext } from "../../entityViews/dashboardConfigurator/context/DashboardConfiguratorContext";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/modern/components/ui/dropdown-menu";
import { EntityRecord } from "@/types/entity";

interface EntityDashboardHeaderProps {
  setConfigOpen: (open: boolean) => void;
}

export function EntityDashboardHeader({ setConfigOpen }: EntityDashboardHeaderProps) {
  const { record, entityConfig } = useEntityManagerContext();
  const { editorHandler } = useSwivuBryntumContext();
  const { activePresetId, activePreset, defaultPresets, customPresets, handleQuickLayoutPresetChange } = useConfiguratorContext();

  const activePresetName = activePreset?.name ?? "HQ Default";

  // Subscribe to record changes so the header re-renders when fields update
  useSwivuStore<EntityRecord>({ storeId: entityConfig.storeKey });

  const openEditor = () => {
    if (record.isTask) {
      editorHandler.openEditor(EDITOR_KEYS.taskEditor, record, { task: record }, { useExisting: true, by: "recordId", className: "dashboard-theme" });
    } else if (record.isResource) {
      editorHandler.openEditor(EDITOR_KEYS.resourceEditorV2, record, { resource: record }, { useExisting: true, by: "recordId", className: "dashboard-theme" });
    } else if (record.isClient) {
      editorHandler.openEditor(EDITOR_KEYS.clientEditorV2, record, { client: record }, { useExisting: true, by: "recordId", className: "dashboard-theme" });
    }
  };

  const { header } = entityConfig;
  const description = header.description(record);
  const featuredItems = header.featuredItems.filter((item) => !!item.getValue(record));
  const metaItems = header.metaItems.filter((item) => !!item.getValue(record));
  const tags = header.tags(record);

  const hasLowerSection = !!description || metaItems.length > 0 || tags.length > 0;

  // Observe a zero-height sentinel placed right after the sticky row.
  // When it scrolls out of view the sticky bar gets its pinned appearance.
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [isPinned, setIsPinned] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  // Single shared state ensures only one dropdown is open at a time.
  // Both dropdowns use modal={false} so clicks propagate through instead of
  // being consumed by Radix's focus-trap overlay — allowing a single click
  // to close the open menu and open the other one simultaneously.
  // See the issue task here https://hq.dev.swivu.com/swivu-engineering-rjv68i/scheduler?entityType=task&entityId=4396887
  const [openMenu, setOpenMenu] = useState<"layout" | "name" | null>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(([entry]) => setIsPinned(!entry.isIntersecting), { threshold: 1, root: sentinel.parentElement, rootMargin: "-40px 0px 0px 0px" });

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [sentinelRef.current]);

  const hoverProps = {
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
  };

  return (
    <>
      {/* ── Sticky title row — direct child of scroll container so it truly sticks ── */}
      <div
        className={cn("sticky top-0 z-10 px-6 py-3 pt-4 flex items-center justify-between gap-4", isPinned && "border-b bg-background")}
        {...hoverProps}
      >
        <div className="flex items-center gap-4 flex-wrap flex-1 min-w-0">
          {/* Title + color dot */}
          <div className="relative flex items-center gap-2 min-w-0 max-w-xl">
            {record.isTask && (
              <ColorPicker value={record?.color} onChange={(color: string | null) => record.set("color", color)} clearable>
                <button
                  type="button"
                  className="relative flex-shrink-0 size-[0.875rem] rounded-full overflow-hidden hover:scale-110 transition-transform cursor-pointer group/color border border-foreground/20"
                  style={{ backgroundColor: record?.color || "transparent" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="absolute inset-0 bg-white/0 group-hover/color:bg-white/20 transition-colors" />
                </button>
              </ColorPicker>
            )}
            <h1 className={cn("text-lg font-normal text-foreground tracking-tight truncate", record.archived && "line-through")} title={record.name || "N/A"}>
              {record.name || "N/A"}
            </h1>
            <button
              type="button"
              onClick={openEditor}
              className={cn("absolute right-0 -top-3 translate-x-full -translate-y-1 h-6 w-6 p-1 z-10 transition-opacity duration-150 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground", isHovered ? "opacity-100" : "opacity-0")}
            >
              <Pencil className="size-3.5" />
            </button>
          </div>

          {/* Location / featured items */}
          {featuredItems.length > 0 && (
            <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
              {featuredItems.map((item, index) => (
                <Tooltip key={index}>
                  {item.renderer ? (
                    <>
                      <TooltipTrigger asChild>
                        <span className="inline-flex items-center">{item.renderer(record, index)}</span>
                      </TooltipTrigger>
                      <TooltipContent>{item.label}</TooltipContent>
                    </>
                  ) : (
                    <>
                      <TooltipTrigger asChild>
                        <span className="inline-flex items-center gap-2 cursor-default">
                          {item.icon}
                          <span>{item.getValue(record)}</span>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>{item.label}</TooltipContent>
                    </>
                  )}
                </Tooltip>
              ))}
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <AppActiveUsers />
          <DropdownMenu open={openMenu === "layout"} onOpenChange={(open) => setOpenMenu(open ? "layout" : null)} modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="focus-visible:ring-0 focus-visible:ring-offset-0 !h-8 gap-1.5 max-w-[160px]">
                <span className="truncate text-sm">{activePresetName}</span>
                <LayoutGrid className="size-3.5 shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52 bg-card">
              <DropdownMenuLabel className="flex items-center justify-between">
                <span className="text-sm font-medium">Layout Preset</span>
              </DropdownMenuLabel>

              <DropdownMenuSeparator />

              <DropdownMenuLabel className="text-xs uppercase text-muted-foreground tracking-wider">Default</DropdownMenuLabel>
              {defaultPresets.map((preset) => (
                <DropdownMenuItem key={preset.id} onClick={() => handleQuickLayoutPresetChange(preset.id)} className="flex items-center justify-between gap-2">
                  {preset.name}
                  {activePresetId === preset.id && <Check className="h-3.5 w-3.5 shrink-0" />}
                </DropdownMenuItem>
              ))}

              {customPresets.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs uppercase text-muted-foreground tracking-wider">My Presets</DropdownMenuLabel>
                  {customPresets.map((preset) => (
                    <DropdownMenuItem key={preset.id} onClick={() => handleQuickLayoutPresetChange(preset.id)} className="flex items-center justify-between gap-2">
                      {preset.name}
                      {activePresetId === preset.id && <Check className="h-3.5 w-3.5 shrink-0" />}
                    </DropdownMenuItem>
                  ))}
                </>
              )}

              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setConfigOpen(true)}>Configure...</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <EntityNameButton open={openMenu === "name"} onOpenChange={(open) => setOpenMenu(open ? "name" : null)} />
        </div>
      </div>

      {/* Sentinel — becomes hidden when sticky row is truly pinned */}
      <div ref={sentinelRef} className="h-0" aria-hidden="true" />

      {/* ── Non-sticky lower section (scrolls away) ── */}
      {/* Always rendered so the border-b is always present */}
      <div
        className={cn("border-b border-border", hasLowerSection ? "px-6 pt-0 pb-4 space-y-3" : "", description && "-mt-3")}
        onDoubleClick={openEditor}
        {...hoverProps}
      >
        {description && (
          <p className="text-xs text-muted-foreground truncate max-w-xl" title={description}>
            {description}
          </p>
        )}

        {(metaItems.length > 0 || tags.length > 0) && <div className="border-t border-border" />}

        {metaItems.length > 0 && (
          <div className="flex items-center gap-4 flex-wrap text-sm text-foreground">
            {metaItems.map((item, index) => (
              <Tooltip key={index}>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center gap-2 cursor-default">
                    {item.icon}
                    <span>{item.getValue(record)}</span>
                  </span>
                </TooltipTrigger>
                <TooltipContent>{item.label}</TooltipContent>
              </Tooltip>
            ))}
          </div>
        )}

        {tags.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <Tooltip>
              <TooltipTrigger asChild>
                <Tag className="w-3.5 h-3.5 text-primary shrink-0 cursor-default" />
              </TooltipTrigger>
              <TooltipContent>Tags</TooltipContent>
            </Tooltip>
            {tags.map((tag, index) => (
              <span key={index} className="px-2 py-0.5 rounded text-sm bg-muted text-muted-foreground whitespace-nowrap">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
